import { Loader2, Sparkles } from "lucide-react";
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@appcaire/ui";
import { trackEvent } from "@/lib/analytics";
import { type PublicAskCaireLocale } from "@/services/public-ask-caire";

// Loaded on first open. The launcher sits in the always-mounted Layout, and
// every page is a React.lazy route chunk (App.tsx), so a static import here
// would be the last path dragging the Ask CAIRE experience — shared FAQ
// catalog included — back into the entry bundle for every page.
const AskCaireExperience = lazy(() =>
  import("./AskCaireExperience").then((module) => ({
    default: module.AskCaireExperience,
  })),
);

const copy = {
  sv: {
    button: "Fråga CAIRE",
    title: "Fråga CAIRE",
    description:
      "Fråga hur Caire fungerar, hur era uppgifter kommer in eller vad Caire faktiskt gör med schemat. Välj en fråga så visar vi svaret — och en bild ur plattformen bredvid.",
  },
  en: {
    button: "Ask CAIRE",
    title: "Ask CAIRE",
    description:
      "Ask how Caire works, how your details get in, or what Caire actually does with the schedule. Pick a question and we'll show the answer — with a look inside the platform beside it.",
  },
} satisfies Record<PublicAskCaireLocale, Record<string, string>>;

// Immersive product-tour pages keep their own bottom-right controls and a
// fullscreen frame; a second floating button there would collide with both.
const SUPPRESSED_PATH_PATTERN =
  /(mobilapp-rundtur|mobile-app-tour|dashboard-rundtur|dashboard-tour|interaktiv-demo|interactive-demo)/;

/**
 * Site-wide "Fråga CAIRE" launcher: a text-labelled floating button, bottom
 * right on every marketing page. It opens the shared Ask CAIRE experience in
 * a dialog, including on the product-led homepage: chat beside product proof.
 *
 * Text label over a bare icon bubble: an unlabelled corner circle is the one
 * chat pattern visitors have learned to ignore.
 *
 * It wears the AI look, not the coral action fill: coral marks human action
 * and aurora marks AI provenance. Like every Fråga CAIRE entry point in the
 * app (`AiActionButton`, see the UX design system), it is ink text on a white
 * surface with an aurora edge and glow and an aurora mark before the label;
 * the aurora never fills the surface. White text on a full aurora fill would
 * also fail contrast on the gradient's cyan end.
 */
export function AskCaireLauncher() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const launcherRef = useRef<HTMLButtonElement | null>(null);

  // The launcher lives in the always-mounted Layout, so the dialog would
  // otherwise stay open across a navigation started by its own footer links
  // ("Boka en demo", "Alla frågor och svar") and bury the destination page.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const locale: PublicAskCaireLocale = location.pathname.startsWith("/en")
    ? "en"
    : "sv";
  const c = copy[locale];

  if (SUPPRESSED_PATH_PATTERN.test(location.pathname)) {
    return null;
  }

  const handleClick = () => {
    trackEvent("ask_caire_launcher", {
      locale,
      page_path: location.pathname,
      action: "open",
    });
    setOpen(true);
  };

  return (
    <>
      <button
        ref={launcherRef}
        type="button"
        onClick={handleClick}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="group fixed bottom-5 right-4 z-40 rounded-full bg-aurora p-[2px] shadow-[0_12px_32px_-12px_rgba(99,102,241,0.6)] transition hover:shadow-[0_14px_36px_-10px_rgba(99,102,241,0.7)] focus:outline-none focus-visible:ring-2 focus-visible:ring-aurora-blue focus-visible:ring-offset-2 motion-reduce:transition-none sm:bottom-6 sm:right-6"
      >
        <span className="inline-flex items-center gap-2 rounded-full bg-white py-2 pl-2 pr-4 text-sm font-semibold text-ink transition group-hover:bg-sand motion-reduce:transition-none">
          <span
            aria-hidden="true"
            className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-aurora text-white"
          >
            <Sparkles className="h-3.5 w-3.5" />
          </span>
          {c.button}
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-6xl bg-caire-page sm:rounded-2xl"
          // A stray tap outside a near-fullscreen dialog would silently throw
          // away the conversation and any typed draft; closing stays on the X
          // and Escape, which are deliberate.
          onInteractOutside={(event) => event.preventDefault()}
          // Without a DialogTrigger, Radix restores focus to whatever was
          // focused at open time — in Safari/Firefox a mouse click leaves that
          // as <body>. Send it back to the launcher explicitly.
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            launcherRef.current?.focus();
          }}
        >
          <DialogTitle className="flex items-center gap-2 text-caire-ink">
            <Sparkles className="h-4 w-4 text-aurora-text" aria-hidden="true" />
            {c.title}
          </DialogTitle>
          <DialogDescription className="max-w-2xl text-[var(--cb-muted)]">
            {c.description}
          </DialogDescription>
          <Suspense
            fallback={
              <div
                className="flex min-h-[16rem] items-center justify-center"
                role="status"
                aria-live="polite"
              >
                <Loader2
                  className="h-6 w-6 animate-spin text-aurora-text"
                  aria-hidden="true"
                />
                <span className="sr-only">
                  {locale === "sv" ? "Laddar…" : "Loading…"}
                </span>
              </div>
            }
          >
            <AskCaireExperience
              locale={locale}
              composerId="ask-caire-launcher-prompt"
              variant="overlay"
            />
          </Suspense>
        </DialogContent>
      </Dialog>
    </>
  );
}
